import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";

import type { IMeasuresResponse } from "../../scheme/components/analytics/interfaces";
import type { IStructureTreePoint } from "../types";
import { MULTI_ANALYSIS_MEASURE_TYPE } from "../types";
import { buildDataMarksFromMeasureResponse } from "../utils/data-marks";
import { normalizeMultiAnalysisDataMarkDates } from "../utils/range-frame";
import { mapMultiAnalysisMeasureTypeToAnalytics } from "../utils/measure-type-map";
import { fetchMultiAnalysisMeasureOptions, fetchMultiAnalysisTrendMeasureData } from "../utils/measure-options";
import {
  normalizeTrendRangeForRequest,
  resolveMultiAnalysisTrendMeasureOptions,
  type IMultiAnalysisMeasureDataResponse,
  type IMultiAnalysisTimeRange,
} from "../utils/trend-series";

interface IUseTimePanelDataMarksArgs {
  enabled: boolean;
  points: readonly IStructureTreePoint[];
  globalRange: IMultiAnalysisTimeRange;
  activeMeasureType: MULTI_ANALYSIS_MEASURE_TYPE | null;
  cellFilters: Record<string, unknown>;
}

interface ILoadTimePanelDataMarksArgs extends IUseTimePanelDataMarksArgs {
  dbTz: number;
}

const flattenMeasureResponse = (
  response: IMultiAnalysisMeasureDataResponse | null | undefined,
): IMeasuresResponse[] => {
  if (!response) {
    return [];
  }

  return Object.values(response).flatMap(measure => (Array.isArray(measure?.data) ? measure.data : []));
};

export const useTimePanelDataMarks = ({
  enabled,
  points,
  globalRange,
  activeMeasureType,
  cellFilters,
}: IUseTimePanelDataMarksArgs): {
  dataMarks: ReturnType<typeof buildDataMarksFromMeasureResponse>;
  isLoading: boolean;
} => {
  const { dbTz } = useAsu();
  const { formatMessage } = useIntl();
  const [dataMarks, setDataMarks] = useState<ReturnType<typeof buildDataMarksFromMeasureResponse>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const { fn: loadDataMarks } = useApiCall(
    async (api, args: ILoadTimePanelDataMarksArgs): Promise<ReturnType<typeof buildDataMarksFromMeasureResponse>> => {
      const pointIds = args.points.map(({ id }) => id);
      const requestRange = normalizeTrendRangeForRequest(args.globalRange, args.dbTz);

      if (!requestRange || !args.activeMeasureType) {
        return [];
      }

      const analyticsMeasureType = mapMultiAnalysisMeasureTypeToAnalytics(args.activeMeasureType);

      const measureOptions = resolveMultiAnalysisTrendMeasureOptions(
        await fetchMultiAnalysisMeasureOptions(api, analyticsMeasureType, pointIds),
        args.activeMeasureType,
        args.cellFilters,
      );
      const measureNames = Array.from(new Set(measureOptions.map(({ title }) => title)));

      if (!measureNames.length) {
        return [];
      }

      const response = await fetchMultiAnalysisTrendMeasureData(api, {
        multiAnalysisMeasureType: args.activeMeasureType,
        pointIds,
        measureNames,
        range: requestRange,
      });

      return normalizeMultiAnalysisDataMarkDates(
        buildDataMarksFromMeasureResponse(
          flattenMeasureResponse(response),
          args.points,
          args.cellFilters,
          direction => formatMessage({ id: `enums.pointDirection.${direction}` }),
          new Map(measureOptions.map(option => [option.title, option.description])),
        ),
        args.dbTz,
      );
    },
    { success: false, error: false },
  );

  const loadDataMarksRef = useRef(loadDataMarks);

  useEffect(() => {
    loadDataMarksRef.current = loadDataMarks;
  }, [loadDataMarks]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || !points.length || !activeMeasureType) {
      setDataMarks([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void loadDataMarksRef
      .current(undefined, {
        enabled,
        points,
        globalRange,
        activeMeasureType,
        cellFilters,
        dbTz,
      })
      .then(nextMarks => {
        if (requestIdRef.current === requestId) {
          setDataMarks(nextMarks ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setDataMarks([]);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [activeMeasureType, cellFilters, dbTz, enabled, globalRange, points]);

  return { dataMarks, isLoading };
};
